import {
  Stack,
  Typography,
  Select,
  MenuItem,
  Chip,
  SelectProps,
  ChipProps,
} from '@mui/material';
import { useCurrentReportDate } from '@features/campaigns/subdomains/visitation/hooks/useReports';
import { useAppDispatch } from '@utils/hooks';
import { reportSelected } from '@components/Dashboard/dashboardSlice';
import { parseReportDateToLocaleFormat } from '@utils/dates';
import { Temporal } from 'temporal-polyfill';
import { unifiedPlatformColorPalette } from '@utils/unifiedPlatformColorPalette';
import { ChannelFilter } from './DateFilter/ChannelFilter';
import React, { useMemo } from 'react';

const buildStatusChipProps = (
  campaignEndDate: string
): Pick<ChipProps, 'sx' | 'label'> =>
  Temporal.PlainDate.compare(
    Temporal.Now.plainDateISO(),
    Temporal.PlainDate.from(campaignEndDate)
  ) > 0
    ? {
        sx: {
          backgroundColor: unifiedPlatformColorPalette.lightActionActive,
        },
        label: 'Campaign Complete',
      }
    : {
        sx: { backgroundColor: unifiedPlatformColorPalette.lightSuccess },
        label: 'Campaign Active',
      };

type DateFilterProps = {
  campaignId: number;
  measurementStartDate: string;
  campaignEndDate: string;
  endReportDates: readonly string[];
  channels: readonly string[];
  children?: React.ReactNode;
};

export const DateFilter = (props: DateFilterProps) => {
  const { measurementStartDate } = props;
  const dispatch = useAppDispatch();
  const selectedReportDate = useCurrentReportDate();
  const statusChipProps = buildStatusChipProps(props.campaignEndDate);
  const endReportDates = useMemo(
    () => props.endReportDates.toSorted((a, b) => b.localeCompare(a)),
    [props.endReportDates]
  );

  const endReportDateOptions = endReportDates.map(reportDate => (
    <MenuItem key={reportDate} value={reportDate}>
      {parseReportDateToLocaleFormat(reportDate)}
    </MenuItem>
  ));

  const handleEndDateChange: SelectProps['onChange'] = event => {
    dispatch(reportSelected(event.target.value as string));
  };

  return (
    <Stack direction="row" alignItems="center" gap={1}>
      <ChannelFilter channels={props.channels} campaignId={props.campaignId} />
      <Typography>Show results from</Typography>
      {/* Change to an input when variable start date is supported */}
      <Typography>
        {parseReportDateToLocaleFormat(measurementStartDate)}
      </Typography>
      <Typography>to</Typography>
      <Select
        sx={{ width: '220px', backgroundColor: 'white' }}
        size="small"
        value={selectedReportDate ?? ''}
        onChange={handleEndDateChange}
        disabled={endReportDates.length === 0}
      >
        {endReportDateOptions}
      </Select>
      <Chip
        label={statusChipProps.label}
        size="small"
        sx={{
          color: 'white',
          px: '0.25rem',
          fontWeight: 500,
          ...statusChipProps.sx,
        }}
      />
      {props.children}
    </Stack>
  );
};
